/**
 * AmazonAddSheet - Amazon の本の URL から書誌を取得してお気に入りに追加する BottomSheet
 *
 * 図書館の蔵書にまだ無い新刊を候補に入れるために使う。
 * Kindle 版の URL はバックエンドで紙の本のページを辿って ISBN を取る。
 */

import { isAxiosError } from 'axios'
import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { previewAmazonBook } from '@/api/services/library-favorite'
import type { LibraryFavoriteInput } from '@/api/types/library-favorite'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useLibraryFavoritesStore } from '@/store/useLibraryFavoritesStore'
import { normalizeIsbn } from '@/store/useLibrarySearchStore'

import { getFavoriteStatusMeta } from '../lib/favorite-status'
import { BookCover } from './BookCover'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** 開いた時点で取得を始める URL (検索欄に貼られた URL など)。空なら入力待ちで開く */
  initialUrl: string
  /** お気に入りに追加できたときに呼ばれる */
  onAdded: () => void
}

type PreviewState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; book: LibraryFavoriteInput }

const fetchPreview = async (url: string): Promise<PreviewState> => {
  try {
    return { kind: 'ready', book: await previewAmazonBook(url) }
  } catch (e) {
    const detail: unknown = isAxiosError(e) ? e.response?.data?.detail : null
    return { kind: 'error', message: typeof detail === 'string' ? detail : '書誌の取得に失敗しました' }
  }
}

export const AmazonAddSheet = ({ open, onOpenChange, initialUrl, onAdded }: Props) => {
  const [url, setUrl] = useState(initialUrl)
  const [preview, setPreview] = useState<PreviewState>(initialUrl ? { kind: 'loading' } : { kind: 'idle' })
  const [adding, setAdding] = useState(false)
  const toggleFavorite = useLibraryFavoritesStore((s) => s.toggle)
  const existing = useLibraryFavoritesStore((s) => {
    if (preview.kind !== 'ready') return null
    const key = normalizeIsbn(preview.book.isbn)
    return key ? (s.byIsbn[key] ?? null) : null
  })

  // 親が key で作り直すため、initialUrl はマウント中に変わらない
  useEffect(() => {
    if (!initialUrl) return
    let cancelled = false
    void fetchPreview(initialUrl).then((next) => {
      if (!cancelled) setPreview(next)
    })
    return () => {
      cancelled = true
    }
  }, [initialUrl])

  const loading = preview.kind === 'loading'

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    const target = url.trim()
    if (!target || loading) return
    setPreview({ kind: 'loading' })
    void fetchPreview(target).then(setPreview)
  }

  const handleAdd = async () => {
    if (preview.kind !== 'ready' || adding) return
    setAdding(true)
    const ok = await toggleFavorite(preview.book)
    setAdding(false)
    if (!ok) {
      toast.error(useLibraryFavoritesStore.getState().error ?? 'お気に入りに追加できませんでした')
      return
    }
    toast.success('お気に入りに追加しました')
    onAdded()
    onOpenChange(false)
  }

  return (
    <BottomSheet title="Amazon の URL から追加" open={open} onOpenChange={onOpenChange} snapPoints={[0.7]}>
      <div className="flex flex-col gap-4 px-4 pb-4">
        <p className="text-xs text-muted-foreground">
          図書館にまだ無い新刊も候補に追加できます。Kindle 版の URL の場合は紙の本の ISBN で登録します。
        </p>

        <form onSubmit={handleSubmit} className="flex gap-2">
          <Input
            type="url"
            inputMode="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.amazon.co.jp/dp/..."
            className="flex-1"
          />
          <Button type="submit" disabled={!url.trim() || loading}>
            {loading ? <Loader2 className="size-4 animate-spin" /> : '取得'}
          </Button>
        </form>

        {loading && <div className="text-sm text-muted-foreground">Amazon から書誌を取得中…</div>}

        {preview.kind === 'error' && (
          <div className="rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-500">
            {preview.message}
          </div>
        )}

        {preview.kind === 'ready' && (
          <div className="flex flex-col gap-3">
            <div className="flex gap-3 rounded border border-border/40 p-3">
              <BookCover isbn={preview.book.isbn} className="h-24 w-16" />
              <div className="flex min-w-0 flex-1 flex-col gap-1 text-sm">
                <div className="font-medium wrap-break-word">{preview.book.title}</div>
                {preview.book.author && <div className="text-xs text-muted-foreground">{preview.book.author}</div>}
                <div className="flex flex-wrap gap-x-2 text-xs text-muted-foreground">
                  {preview.book.publisher && <span>{preview.book.publisher}</span>}
                  {preview.book.pubdate && <span>{preview.book.pubdate}</span>}
                </div>
                <div className="text-xs text-muted-foreground">ISBN {preview.book.isbn}</div>
              </div>
            </div>

            {existing ? (
              <div className="text-center text-sm text-muted-foreground">
                お気に入りに登録済みです（{getFavoriteStatusMeta(existing.status).label}）
              </div>
            ) : (
              <Button type="button" onClick={() => void handleAdd()} disabled={adding}>
                {adding && <Loader2 className="size-4 animate-spin" />}
                お気に入りに追加
              </Button>
            )}
          </div>
        )}
      </div>
    </BottomSheet>
  )
}
